import React, { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { cx } from '@linaria/core';
import { styled } from '@linaria/react';
import { DateGuestInput } from 'components/Accommodation/DateGuestInput';
import { SelectedElementType } from 'components/Accommodation/DateGuestInput/ReviewsTrackClick';
import { HotelDescriptions } from 'components/Accommodation/HotelDescriptions/HotelDescriptions';
import { HotelDetails } from 'components/Accommodation/HotelDetails';
import { HotelLocationMap } from 'components/Accommodation/HotelLocationMap';
import { LocationSummary } from 'components/Accommodation/LocationSummary';
import { MosaicGallery } from 'components/Accommodation/MosaicGallery';
import { Offers } from 'components/Accommodation/Offers';
import { ReloadAP } from 'components/Accommodation/ReloadAPOverlay/ReloadAP';
import { ReviewSummary } from 'components/Accommodation/ReviewSummary';
import { StreetViewOverlay } from 'components/Accommodation/StreetViewOverlay';
import { WrapperWithPadding } from 'components/Accommodation/WrapperWithPadding';
import { PriceWatchCTAWrapper } from 'components/PriceWatchCTAWrapper/PriceWatchCTAWrapper';
import { ScrollSection } from 'components/ScrollSpy';
import { SplitBookingDetails } from 'components/SplitBookings/SplitBookingDetails';
import { VacationRentalDetails } from 'components/VacationRentalDetails';
import { OfferViewContext, OfferViewType } from 'context/offerViewContext';
import { useDeferredScrollToApTarget } from 'hooks/useDeferredScrollToApTarget';
import placeHolderImage from 'images/placeholder-hotel-image.svg';
import { trackAccommodationPageReady } from 'middleware/analytics/actions/trackEvent';
import { AccommodationPageNavSections } from 'modules/accommodation/types';
import { isVioOfferOrBundleSelected } from 'modules/accommodation/utils';
import { getUserAgentDetails } from 'modules/meta/selectors';
import { showGuestReviewsOverlay } from 'modules/overlay/actions';
import { DateGuestPickerSource } from 'modules/overlay/types';
import { getAnchorHotelId, getSplitBookingDetails } from 'modules/sapiSearch/selectors';
import { getIsHotelDetailsOpenOnLanding } from 'modules/search/selectors';
import { toggle } from 'opticks';
import { getRankedFacilitiesFromContentHotel } from 'utils/facilities';
import { getIsStreetViewEnabled } from 'utils/map';
import { Card } from '@daedalus/atlas/Card/Card';
import { useDeviceLayout } from '@daedalus/atlas/context/deviceLayout';
import { Stack } from '@daedalus/atlas/helpers/Stack';
import { useBrand } from '@daedalus/core/src/_web/brand/hooks/useBrand';
import { useDispatchTeamEvent } from '@daedalus/core/src/analytics/components/TeamContext';
import { trackEvent } from '@daedalus/core/src/analytics/modules/actions';
import { Action, Category, Entity } from '@daedalus/core/src/analytics/types/Events';
import { trackAccommodationPageFRP } from '@daedalus/core/src/analytics/utils/trackPerformanceMetrics';
import { getLanguageCode } from '@daedalus/core/src/localization/services/locale';
import { OldHotelOfferEntity } from '@daedalus/core/src/offer/business/offersMapping';
import { useGetUserReviewsQuery } from '@daedalus/core/src/sapi/services/searchApi';
import { PriceWatchToggle } from '@daedalus/shared/src/priceWatch/components/PriceWatchToggle/PriceWatchToggle';
import { TopRoomsOrUnavailability } from '../../Rooms/TopRoomsOrUnavailability/TopRoomsOrUnavailability';
import { AccommodationFooterContainer } from './AccommodationFooterContainer';
import { ConditionalFacilities } from './ConditionallyRenderedSections/ConditionalFacilities';
import { ConditionalPolicies } from './ConditionallyRenderedSections/ConditionalPolicies';
import { ConditionalReviews } from './ConditionallyRenderedSections/ConditionalReviews';
import { ConditionalRooms } from './ConditionallyRenderedSections/ConditionalRooms';
import { BoxSectionPadding, MainContentContainer, Wrapper } from './styles';
import type { ContentHotel, VrDetails } from '@findhotel/sapi/dist/types/packages/core/src';
interface Hotel extends ContentHotel {
  isVr?: boolean;
  vrDetails?: VrDetails;
}
type Props = {
  /**
   * Note that this could be mapped from SRP data in which case policies (and some other) data will be missing
   */
  hotel: Hotel;
  hotelId: string;
};
const _exp = /*#__PURE__*/() => VacationRentalDetails;
const VacationRentalDetailsComponent = /*#__PURE__*/styled(_exp())({
  name: "VacationRentalDetailsComponent",
  class: "v1a2s0g9",
  propsAsIs: true
});
const PlaceholderImageElement = /*#__PURE__*/styled('img')({
  name: "PlaceholderImageElement",
  class: "p1le7hi6",
  propsAsIs: false
});
const TopOffersList = ({
  hotel,
  hotelId,
  isLoading
}: {
  hotel: ContentHotel | undefined;
  hotelId: string;
  offerEntity?: OldHotelOfferEntity;
  isLoading?: boolean;
}) => {
  return <BoxSectionPadding>
      <Stack space="s400">
        <DateGuestInput source={DateGuestPickerSource.AccommodationPageSearch} isMobile />
        <PriceWatchCTAWrapper hotel={hotel} ctaComponent={PriceWatchToggle} componentName="PriceWatchToggle" holdOutElement={null} searchIsCompleted={!isLoading} />
        <Offers hotelId={hotelId} />
      </Stack>
    </BoxSectionPadding>;
};
export const AccommodationView = ({
  hotel,
  hotelId
}: Props) => {
  useDeferredScrollToApTarget();
  const {
    isMobile
  } = useDeviceLayout();
  const {
    isSafari
  } = useSelector(getUserAgentDetails);
  const locationRef = useRef<HTMLDivElement>();
  const reviewsRef = useRef<HTMLDivElement>();
  const dispatch = useDispatch();
  const dispatchTeamEvent = useDispatchTeamEvent();
  const languageCode = useSelector(getLanguageCode);
  const isEnglishLanguage = languageCode.startsWith('en');
  const isStreetViewEnabled = getIsStreetViewEnabled(hotel?.country);
  const hotelDetailsIsOpenOnLanding = useSelector(getIsHotelDetailsOpenOnLanding); // check if we have `openHotelDetails=1` in url params which means it's a GVR (Google vacation rentals)
  const rankedFacilities = getRankedFacilitiesFromContentHotel(hotel);
  const onLocationSummaryClick = () => {
    locationRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
    dispatchTeamEvent(trackEvent({
      category: Category.User,
      entity: Entity.HotelDetailsLocationSummary,
      action: Action.Clicked
    }));
  };
  const anchorHotelId = useSelector(getAnchorHotelId);
  const isAnchor = hotelId === anchorHotelId;
  const {
    hasSplitBookingOffer
  } = useSelector(state => getSplitBookingDetails(state, hotelId));
  const isVioOfferSelected = isVioOfferOrBundleSelected();
  const {
    brandIsVio
  } = useBrand();
  const isGuestReviewsLanguageBSide = toggle('f72fa251-guest-reviews-languages', false, true);
  const hasGuestReviews = isGuestReviewsLanguageBSide && brandIsVio || !isGuestReviewsLanguageBSide && brandIsVio && isEnglishLanguage;
  const {
    data
  } = useGetUserReviewsQuery({
    hotelId,
    language: languageCode
  }, {
    skip: !hasGuestReviews || !hotelId
  });
  const guestReviewsData = useMemo(() => {
    if (!data?.reviews || data.reviews.length === 0) return [];
    if (isGuestReviewsLanguageBSide) return data.reviews;
    return data.reviews.filter(review => review.language === 'en');
  }, [data, isGuestReviewsLanguageBSide]);
  const onReviewSummaryClick = () => {
    if (typeof guestReviewsData === 'undefined') {
      reviewsRef.current?.scrollIntoView({
        behavior: 'smooth'
      });
    } else {
      dispatch(trackEvent({
        category: Category.User,
        entity: Entity.ReviewDetails,
        action: Action.Clicked,
        payload: {
          selectedElement: SelectedElementType.ReadAllReviews
        }
      }));
      dispatch(showGuestReviewsOverlay());
    }
    dispatchTeamEvent(trackEvent({
      category: Category.User,
      entity: Entity.HotelDetailsReviewSummary,
      action: Action.Clicked,
      payload: {
        selectedElement: SelectedElementType.Rating
      }
    }));
  };
  useEffect(() => {
    dispatch(trackAccommodationPageReady());
  }, [dispatch]);
  useEffect(() => {
    trackAccommodationPageFRP(dispatch);
  }, [dispatch]);
  const hotelImages = hotel?.imageURIs;
  const renderRoomsAndOffersContent = () => {
    if (isVioOfferSelected) {
      return <TopRoomsOrUnavailability hotelId={hotelId} />;
    }
    return <TopOffersList hotel={hotel} hotelId={hotelId} />;
  };
  return <OfferViewContext.Provider value={OfferViewType.AP_OVERLAY}>
      {hotelImages?.length === 0 ? <PlaceholderImageElement src={placeHolderImage} alt="placeholder" /> : <MosaicGallery images={hotelImages || []} hotelId={hotelId} />}
      <MainContentContainer className={cx(isSafari && '__safari')}>
        <Card borderRadius="xxl" hasRoundedBottom={false} shadow="fixedReverse">
          <Wrapper>
            <WrapperWithPadding paddingTop="s500" paddingBottom="s000">
              <div>
                <HotelDetails hotel={hotel} />
                <LocationSummary hotel={hotel} onLocationSummaryClick={onLocationSummaryClick} />
                <ScrollSection name={AccommodationPageNavSections.ReviewsSummary}>
                  <ReviewSummary hotel={hotel} onReviewSummaryClick={onReviewSummaryClick} />
                </ScrollSection>
                {hotelDetailsIsOpenOnLanding && isAnchor && <VacationRentalDetailsComponent showVrDetails={hotel?.isVr} vrDetails={hotel?.vrDetails || {}} rankedFacilities={rankedFacilities} />}
              </div>
              <Stack space="s300">{renderRoomsAndOffersContent()}</Stack>
            </WrapperWithPadding>
            {toggle('sapi4eva-hotel-descriptions', null, <>
                <HotelDescriptions hotel={hotel as ContentHotel} />
              </>)}
            <WrapperWithPadding ref={locationRef}>
              <HotelLocationMap hotel={hotel} />
            </WrapperWithPadding>
            <ConditionalFacilities hotel={hotel} />
            <ConditionalRooms hotelId={hotelId} />
            <ConditionalPolicies hotel={hotel} />
            <ConditionalReviews hotel={hotel} reviewsRef={reviewsRef} />
          </Wrapper>
        </Card>
        <OfferViewContext.Provider value={OfferViewType.AP_FOOTER}>
          <AccommodationFooterContainer hotelId={hotelId} />
        </OfferViewContext.Provider>
      </MainContentContainer>

      {hasSplitBookingOffer && isAnchor && <SplitBookingDetails isAccommodationPage />}
      {isStreetViewEnabled && isMobile && <StreetViewOverlay />}
      <ReloadAP />
    </OfferViewContext.Provider>;
};

require("./AccommodationView.wyw-in-js.css!=!../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./AccommodationView.tsx");